/**
 * Photo Gallery Component.
 *
 * Image-focused attachment viewer with grid layout, lightbox preview,
 * camera capture support (mobile), drag-to-upload, image limit
 * enforcement, and primary image selection.
 */

import { useState, useRef, useEffect } from "react";
import { Camera, Upload, X, ZoomIn, Trash2, Star } from "lucide-react";
import {
  useAttachments,
  useUploadAttachment,
  useDeleteAttachment,
} from "@/features/attachments/api";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";

/**
 * Loads an image via Axios (which attaches the auth token) and converts
 * the response to a blob URL that <img> tags can render.
 */
function AuthImage({
  attachmentId,
  alt,
  className,
}: {
  attachmentId: string;
  alt: string;
  className?: string;
}) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let revoke: string | null = null;
    apiClient
      .get(`/api/v1/attachments/${attachmentId}`, { responseType: "blob" })
      .then((resp) => {
        const url = URL.createObjectURL(resp.data as Blob);
        revoke = url;
        setSrc(url);
      })
      .catch(() => setSrc(null));

    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [attachmentId]);

  if (!src) {
    return (
      <div
        className={`${className || ""} flex items-center justify-center bg-[var(--surface-sunken)]`}
      >
        <div className="h-4 w-4 border-2 border-[var(--text-tertiary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return <img src={src} alt={alt} className={className} />;
}

interface PhotoGalleryProps {
  entityType: string;
  entityId: string;
  /** Maximum number of images allowed (default: 3). */
  maxImages?: number;
  /** Current primary image ID (first in the images array). */
  primaryImageId?: string | null;
  /** Called when the user sets a new primary image. */
  onSetPrimary?: (attachmentId: string) => void;
  /** Called after a photo is successfully uploaded (with the new attachment ID). */
  onPhotoUploaded?: (attachmentId: string) => void;
  /** Called after a photo is deleted (with the removed attachment ID). */
  onPhotoDeleted?: (attachmentId: string) => void;
}

function PhotoGallery({
  entityType,
  entityId,
  maxImages = 3,
  primaryImageId,
  onSetPrimary,
  onPhotoUploaded,
  onPhotoDeleted,
}: PhotoGalleryProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const { data: attachments, isLoading } = useAttachments(entityType, entityId);
  const uploadMutation = useUploadAttachment();
  const deleteMutation = useDeleteAttachment();
  const [lightboxId, setLightboxId] = useState<string | null>(null);

  const photos =
    attachments?.filter((a) => a.file_type.startsWith("image/")) || [];
  const atLimit = photos.length >= maxImages;

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    const remaining = maxImages - photos.length;
    if (remaining <= 0) {
      toast.info("Image limit reached", `Maximum ${maxImages} photos allowed.`);
      return;
    }
    const filesToUpload = Array.from(files).slice(0, remaining);
    if (filesToUpload.length < files.length) {
      toast.info(
        "Some files skipped",
        `Only ${remaining} more photo${remaining !== 1 ? "s" : ""} allowed.`,
      );
    }
    for (const file of filesToUpload) {
      const result = await uploadMutation.mutateAsync({
        entityType,
        entityId,
        file,
      });
      onPhotoUploaded?.(result.id);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="space-y-4">
      {/* Upload Area — hidden when at limit */}
      {!atLimit && (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="glass rounded-[var(--radius-xl)] p-6 text-center transition-all hover:shadow-md cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="mx-auto h-8 w-8 text-[var(--text-tertiary)]" />
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            {uploadMutation.isPending
              ? "Uploading..."
              : "Drop photos here or click to browse"}
          </p>
          <div className="mt-3 flex justify-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              className="inline-flex items-center gap-1.5 rounded-[var(--radius-lg)] bg-brand-600/10 px-3 py-1.5 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-600/20"
            >
              <Upload className="h-3.5 w-3.5" /> Browse Files
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                cameraInputRef.current?.click();
              }}
              className="inline-flex items-center gap-1.5 rounded-[var(--radius-lg)] bg-brand-600/10 px-3 py-1.5 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-600/20"
            >
              <Camera className="h-3.5 w-3.5" /> Take Photo
            </button>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          if (fileInputRef.current) fileInputRef.current.value = "";
        }}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          if (cameraInputRef.current) cameraInputRef.current.value = "";
        }}
      />

      {/* Photo counter */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-[var(--text-tertiary)]">
          {photos.length} / {maxImages} photo{maxImages !== 1 ? "s" : ""}
        </p>
        {atLimit && (
          <p className="text-[10px] text-[var(--text-tertiary)]">
            Remove a photo to upload a new one
          </p>
        )}
      </div>

      {/* Photo Grid */}
      {isLoading ? (
        <p className="text-sm text-[var(--text-tertiary)]">Loading photos...</p>
      ) : photos.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {photos.map((photo) => {
            // Image loaded via AuthImage component (handles auth token).
            const isPrimary = primaryImageId === photo.id;
            return (
              <div
                key={photo.id}
                className={`group relative aspect-square rounded-[var(--radius-lg)] overflow-hidden glass animate-fade-in ${
                  isPrimary
                    ? "ring-2 ring-brand-500 ring-offset-2 ring-offset-[var(--surface-base)]"
                    : ""
                }`}
              >
                <AuthImage
                  attachmentId={photo.id}
                  alt={photo.file_name}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                {/* Primary badge */}
                {isPrimary && (
                  <div className="absolute top-2 left-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-brand-600/90 px-2 py-0.5 text-[10px] font-semibold text-white">
                      <Star className="h-2.5 w-2.5 fill-current" />
                      Primary
                    </span>
                  </div>
                )}
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 transition-opacity group-hover:opacity-100">
                  <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between p-2">
                    <p className="truncate text-xs text-white/80">
                      {photo.file_name}
                    </p>
                    <div className="flex gap-1">
                      {/* Set as primary */}
                      {onSetPrimary && !isPrimary && (
                        <button
                          onClick={() => onSetPrimary(photo.id)}
                          title="Set as primary image"
                          className="rounded-full bg-[var(--surface-base)]/20 p-1 text-white backdrop-blur-sm transition-colors hover:bg-brand-500/50"
                        >
                          <Star className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => setLightboxId(photo.id)}
                        className="rounded-full bg-[var(--surface-base)]/20 p-1 text-white backdrop-blur-sm transition-colors hover:bg-[var(--surface-base)]/30"
                      >
                        <ZoomIn className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => {
                          deleteMutation.mutate({
                            id: photo.id,
                            entityType,
                            entityId,
                          });
                          onPhotoDeleted?.(photo.id);
                        }}
                        className="rounded-full bg-red-500/30 p-1 text-white backdrop-blur-sm transition-colors hover:bg-red-500/50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-center text-sm text-[var(--text-tertiary)]">
          No photos yet.
        </p>
      )}

      {/* Lightbox */}
      {lightboxId && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md"
          onClick={() => setLightboxId(null)}
        >
          <button className="absolute top-4 right-4 text-white/70 hover:text-white">
            <X className="h-6 w-6" />
          </button>
          <AuthImage
            attachmentId={lightboxId}
            alt="Full view"
            className="max-h-[90vh] max-w-[90vw] rounded-[var(--radius-xl)] shadow-2xl animate-scale-in object-contain"
          />
        </div>
      )}
    </div>
  );
}

export default PhotoGallery;
export { AuthImage };
